<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useDicomMetaStore } from '@/src/store/dicom-web/dicom-meta-store';
import { useDicomWebStore } from '@/src/store/dicom-web/dicom-web-store';
import { fetchInstanceThumbnail, parseUrl } from '@/src/core/dicom-web-api';
import {
  findOriginalPanoramas,
  type OriginalPanoramaImage,
} from '@/src/panoramic/original';
import { populateAuthorizationToken } from '@/src/utils/token';
import ControlButton from '@/src/components/ControlButton.vue';
import IsolatedDialog from '@/src/components/IsolatedDialog.vue';

const props = defineProps<{ studyUid?: string; compact?: boolean }>();
const web = useDicomWebStore();
const loaded = useDICOMStore();
const remote = useDicomMetaStore();
const { currentImageID } = useCurrentImage('global');
const studyUID = computed(() => {
  if (props.studyUid) return props.studyUid;
  if (currentImageID.value)
    return (
      loaded.volumeStudy[currentImageID.value] ??
      remote.volumeStudy[currentImageID.value] ??
      null
    );
  return web.activeVolumeKey
    ? (remote.volumeStudy[web.activeVolumeKey] ?? null)
    : null;
});
const root = computed(() => parseUrl(web.host ?? '').host);
const open = ref(false);
const searching = ref(false);
const loading = ref(false);
const error = ref('');
const imageURL = ref<string | null>(null);
const images = ref<OriginalPanoramaImage[]>([]);
const selected = ref(0);
const zoom = ref(1);
const brightness = ref(1);
const contrast = ref(1);
const offset = ref({ x: 0, y: 0 });
let drag: {
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  pointer: number;
} | null = null;
let generation = 0;
let imageGeneration = 0;

function resetView() {
  zoom.value = 1;
  brightness.value = 1;
  contrast.value = 1;
  offset.value = { x: 0, y: 0 };
}
function releaseImage() {
  if (imageURL.value) URL.revokeObjectURL(imageURL.value);
  imageURL.value = null;
}
function reset() {
  generation++;
  imageGeneration++;
  images.value = [];
  selected.value = 0;
  searching.value = false;
  loading.value = false;
  error.value = '';
  drag = null;
  releaseImage();
  resetView();
}
async function showImage(index: number) {
  const item = images.value[index];
  if (!item) return;
  const request = ++imageGeneration;
  const source = root.value;
  releaseImage();
  resetView();
  error.value = '';
  loading.value = true;
  try {
    const url = await fetchInstanceThumbnail(source, {
      studyInstanceUID: item.studyUID,
      seriesInstanceUID: item.seriesUID,
      sopInstanceUID: item.instanceUID,
    });
    if (request !== imageGeneration) {
      URL.revokeObjectURL(url);
      return;
    }
    imageURL.value = url;
  } catch {
    if (request === imageGeneration)
      error.value =
        'No se pudo cargar la panorámica original. Intente de nuevo.';
  } finally {
    if (request === imageGeneration) loading.value = false;
  }
}
async function search() {
  reset();
  if (!studyUID.value || !root.value) return;
  const request = generation;
  const study = studyUID.value;
  const source = root.value;
  searching.value = true;
  try {
    await populateAuthorizationToken();
    if (request !== generation) return;
    const found = await findOriginalPanoramas(source, study);
    if (request !== generation) return;
    images.value = found;
    if (found.length) await showImage(0);
  } catch {
    if (request === generation)
      error.value =
        'No se pudo consultar la panorámica de este estudio. Revise la conexión o sus permisos.';
  } finally {
    if (request === generation) searching.value = false;
  }
}
watch(selected, (index) => {
  if (images.value.length && open.value) void showImage(index);
});
watch(open, (value) => {
  if (value) void search();
  else reset();
});
watch([studyUID, root], () => {
  open.value = false;
  reset();
});
onBeforeUnmount(reset);
function startDrag(event: PointerEvent) {
  if (event.button !== 0 || !imageURL.value) return;
  drag = {
    x: event.clientX,
    y: event.clientY,
    offsetX: offset.value.x,
    offsetY: offset.value.y,
    pointer: event.pointerId,
  };
  (event.currentTarget as Element).setPointerCapture(event.pointerId);
}
function moveDrag(event: PointerEvent) {
  if (drag?.pointer === event.pointerId)
    offset.value = {
      x: drag.offsetX + event.clientX - drag.x,
      y: drag.offsetY + event.clientY - drag.y,
    };
}
function wheel(event: WheelEvent) {
  zoom.value = Math.max(
    0.5,
    Math.min(5, zoom.value * (event.deltaY > 0 ? 0.9 : 1.1))
  );
}
</script>

<template>
  <template v-if="studyUID && root">
    <ControlButton
      v-if="compact"
      size="40"
      icon="mdi-image-outline"
      name="Ver panorámica original"
      aria-label="Ver panorámica original"
      @click="open = true"
    />
    <v-btn
      v-else
      size="small"
      variant="outlined"
      prepend-icon="mdi-image-outline"
      aria-label="Ver panorámica original"
      @click="open = true"
      >Ver panorámica original</v-btn
    >
    <IsolatedDialog
      v-model="open"
      width="95vw"
      max-width="1600"
      height="88dvh"
      content-class="original-panorama-dialog"
    >
      <v-card class="original-panorama" data-testid="original-panorama">
        <header class="original-panorama__header">
          <div>
            <strong>Panorámica original · 2D</strong
            ><small>Imagen archivada en este estudio</small>
          </div>
          <v-btn
            icon="mdi-close"
            variant="text"
            aria-label="Cerrar panorámica original"
            @click="open = false"
          />
        </header>
        <div v-if="images.length > 1" class="original-panorama__selection">
          <label
            >Imagen
            <select
              v-model.number="selected"
              aria-label="Seleccionar panorámica original"
            >
              <option
                v-for="(item, index) in images"
                :key="item.instanceUID"
                :value="index"
              >
                {{ item.label }}
              </option>
            </select></label
          >
        </div>
        <div
          class="original-panorama__viewport"
          @wheel.prevent="wheel"
          @pointerdown.prevent="startDrag"
          @pointermove="moveDrag"
          @pointerup="drag = null"
          @pointercancel="drag = null"
          @lostpointercapture="drag = null"
        >
          <img
            v-if="imageURL"
            :src="imageURL"
            alt="Radiografía panorámica original del estudio"
            draggable="false"
            :style="{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              filter: `brightness(${brightness}) contrast(${contrast})`,
            }"
            @error="error = 'No se pudo mostrar la imagen de la panorámica.'"
          />
          <div
            v-if="searching || loading"
            class="original-panorama__message"
            role="status"
          >
            <v-progress-circular indeterminate />
            <p>
              {{
                searching
                  ? 'Buscando panorámica original…'
                  : 'Cargando panorámica…'
              }}
            </p>
          </div>
          <div
            v-else-if="error"
            class="original-panorama__message"
            role="alert"
          >
            <p>{{ error }}</p>
            <v-btn @click="search">Reintentar</v-btn>
          </div>
          <div
            v-else-if="!images.length"
            class="original-panorama__message"
            role="status"
          >
            <strong
              >No se encontró una panorámica original 2D identificada en este
              estudio.</strong
            >
            <p>
              Debe estar incluida como imagen o serie panorámica en el archivo
              DICOM.
            </p>
          </div>
        </div>
        <footer v-if="imageURL" class="original-panorama__controls">
          <label
            >Zoom
            <input
              v-model.number="zoom"
              aria-label="Zoom de panorámica original"
              type="range"
              min="0.5"
              max="5"
              step="0.05" /></label
          ><label
            >Brillo
            <input
              v-model.number="brightness"
              aria-label="Brillo de panorámica original"
              type="range"
              min="0.25"
              max="3"
              step="0.05" /></label
          ><label
            >Contraste
            <input
              v-model.number="contrast"
              aria-label="Contraste de panorámica original"
              type="range"
              min="0.25"
              max="3"
              step="0.05" /></label
          ><v-btn size="small" @click="resetView">Ajustar a pantalla</v-btn
          ><span>Arrastre para desplazar · Rueda para ampliar</span>
        </footer>
      </v-card>
    </IsolatedDialog>
  </template>
</template>

<style scoped>
.original-panorama {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #09141d;
  color: #e4edf4;
}
.original-panorama__header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #28404e;
}
.original-panorama__header small {
  display: block;
  color: #9eb5c4;
  margin-top: 4px;
}
.original-panorama__viewport {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: black;
  touch-action: none;
  cursor: grab;
}
.original-panorama__viewport img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
}
.original-panorama__message {
  position: absolute;
  inset: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  background: black;
}
.original-panorama__controls {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  padding: 12px 16px;
  font-size: 0.8rem;
}
.original-panorama__controls label {
  display: flex;
  align-items: center;
  gap: 8px;
}
.original-panorama__controls input {
  width: 100px;
  accent-color: #57d9f1;
}
.original-panorama__controls span {
  color: #9eb5c4;
}
.original-panorama__selection {
  flex-shrink: 0;
  padding: 8px 16px;
}
.original-panorama__selection select {
  border: 1px solid #456273;
  padding: 4px 8px;
  max-width: 80vw;
  color: white;
  background: #142d3c;
}
</style>
